import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

// Check whether the browser supports passive events
// See more on https://developers.google.com/web/updates/2017/01/scrolling-intervention
let supportsPassive;
const applyPassive = () => {
  if (supportsPassive !== undefined) {
    return supportsPassive ? { passive: false } : false;
  }
  supportsPassive = false;
  try {
    document.addEventListener('testPassive', null, {
      // eslint-disable-next-line getter-return
      get passive() {
        supportsPassive = true;
      }
    });
  } catch (e) { /* silent catch */
  }
  return applyPassive();
};

// Get appropriate vendor prefixed name if standard one is not supported
const transformProp = (() => {
  const testEl = document.createElement('div');
  if (testEl.style.transform == null) {
    const vendors = ['Webkit', 'Moz', 'ms'];
    for (let i = 0; i < vendors.length; i++) {
      if (testEl.style[`${vendors[i]}Transform`] !== undefined) {
        return `${vendors[i]}Transform`;
      }
    }
  }
  return 'transform';
})();

class Drawer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      animating: false
    };

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.update = this.update.bind(this);
    this.addEventListeners = this.addEventListeners.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);

    this.startX = 0;
    this.pointX = 0;
    this.pointY = 0;
    this.stepsX = 0;
    this.stepsY = 0;
    this.directionX = 0;
    this.directionLocked = false;
    this.currentX = 0;

    this.initiated = false;

    this.drawerEl = undefined;
    this.containerEl = undefined;
    this.toggle = this.toggle.bind(this);
    props.registerToggle(props.name, this.toggle);
  }

  componentDidMount() {
    this.addEventListeners();
  }

  componentWillUnmount() {
    this.removeEventListeners();
    this.close(false);
  }

  toggle() {
    if (this.state.open) {
      this.close();
    } else {
      this.open();
    }
  }

  onTransitionEnd() {
    this.setState({ animating: false });
    this.drawerEl.removeEventListener('transitionend', this.onTransitionEnd);
  }

  onTouchStart(evt) {
    if (!this.state.open) return;

    const point = evt.touches[0];

    this.startX = point.pageX;
    this.pointX = point.pageX;
    this.pointY = point.pageY;
    this.stepsX = 0;
    this.stepsY = 0;
    this.directionX = 0;

    this.directionLocked = false;

    this.currentX = this.startX;

    this.initiated = true;
    requestAnimationFrame(this.update);
  }

  onTouchMove(evt) {
    if (!this.initiated) return;

    const point = evt.touches[0];
    const deltaX = point.pageX - this.pointX;
    const deltaY = point.pageY - this.pointY;

    this.pointX = point.pageX;
    this.pointY = point.pageY;
    this.stepsX += Math.abs(deltaX);
    this.stepsY += Math.abs(deltaY);

    // swipe direction
    if (this.stepsX < 10 && this.stepsY < 10) {
      return;
    }

    if (!this.directionLocked && this.stepsY > this.stepsX) {
      this.initiated = false;
      return;
    }

    evt.preventDefault();
    this.directionLocked = true;

    this.currentX = evt.touches[0].pageX;
  }

  onTouchEnd() {
    if (!this.initiated) return;

    const {
      panCloseMask = 50,
      rightSide
    } = this.props;

    this.initiated = false;

    const translateX = Math.abs(
      rightSide
        ? Math.max(0, this.currentX - this.startX)
        : Math.min(0, this.currentX - this.startX)
    );
    this.containerEl.style[transformProp] = '';

    if (translateX > panCloseMask) {
      this.close();
    } else {
      this.open();
    }
  }

  addEventListeners() {
    this.containerEl.addEventListener('touchstart', this.onTouchStart, applyPassive());
    this.drawerEl.addEventListener('touchmove', this.onTouchMove, applyPassive());
    this.containerEl.addEventListener('touchend', this.onTouchEnd);
  }

  removeEventListeners() {
    this.containerEl.removeEventListener('touchstart', this.onTouchStart);
    this.drawerEl.removeEventListener('touchmove', this.onTouchMove);
    this.containerEl.removeEventListener('touchend', this.onTouchEnd);
  }

  update() {
    if (!this.initiated) return;

    requestAnimationFrame(this.update);
    const { width } = this.drawerEl.getBoundingClientRect();
    const translateX = this.props.rightSide
      ? Math.max((10 * width) / 100, this.currentX - this.startX)
      : Math.min(0, this.currentX - this.startX);
    this.containerEl.style[transformProp] = `translateX(${translateX}px)`;
  }

  /* eslint-disable class-methods-use-this */
  blockClicks(evt) {
    evt.stopPropagation();
  }

  /* eslint-enable class-methods-use-this */

  close(animating = true) {
    this.setState({
      open: false,
      animating
    });
    document.body.style.overflow = '';

    if (animating) {
      this.drawerEl.addEventListener('transitionend', this.onTransitionEnd);
    }
  }

  open() {
    this.setState({
      open: true,
      animating: true
    });
    document.body.style.overflow = 'hidden';
    this.drawerEl.addEventListener('transitionend', this.onTransitionEnd);
  }

  render() {
    const { open, animating } = this.state;
    const {
      children,
      rightSide
    } = this.props;

    const sideNavClass = classNames({
      drawer: true,
      'drawer--visible': open,
      'drawer--animating': animating
    });

    const containerClassNames = classNames({
      drawer__container: true,
      'drawer__container--right': rightSide
    });

    return (
      <aside
        className={sideNavClass}
        onClick={this.close}
        ref={(ref) => {
          this.drawerEl = ref;
        }}
      >
        <div
          className={containerClassNames}
          onClick={this.blockClicks}
          ref={(ref) => {
            this.containerEl = ref;
          }}
        >
          {children}
        </div>
      </aside>
    );
  }
}

Drawer.propTypes = {
  children: PropTypes.node,
  panCloseMask: PropTypes.number,
  registerToggle: PropTypes.func,
  name: PropTypes.string.isRequired,
  rightSide: PropTypes.bool
};

Drawer.defaultProps = {
  rightSide: false
};

export default Drawer;
